<template>
  <b-container fluid>
    <brands-filter
      :panel.sync="activePanel"
      :filter-active="(selBrands.length > 0 && selBrands.length < elevatorBrands.length) || (selModelTypes.length > 0 && selModelTypes.length < elevatorModelTypes.length)"
      :brands="elevatorBrands"
      :selected-brands.sync="selectedBrands"
      :model-types="elevatorModelTypes"
      :selected-model-types.sync="selectedModelTypes"
      name-prefix="maintenance"
      row-class="mb-2 align-items-center"
      panel-spacing="mb-2"
      @invert="invertBrands()"
      @all="onBrandsAll()"
    >
      <template #search>
        <clearable-input v-model="textFilter" placeholder="Filter..."></clearable-input>
      </template>
    </brands-filter>

    <b-row class="mt-3 align-items-center">
      <b-col>
        <h2>Maintenance</h2>
      </b-col>
      <b-col cols="auto">
        <b-button v-if="canManageMaintenance" variant="secondary" @click="$bvModal.show('maint-settings')">Settings</b-button>
      </b-col>
    </b-row>

    <b-row align-v="center" class="mt-2">
      <b-col sm="2">
        From: <date-picker :value="fromDate" @change="setFrom" />
      </b-col>
      <b-col sm="2">
        To: <date-picker :value="toDate" @change="setTo" />
      </b-col>
    </b-row>

    <b-row class="mt-2">
      <b-col>
        <b-button size="sm" class="mt-1" @click="setRange('today')">Today</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('tomorrow')">Tomorrow</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisWeek')">This Week</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('nextWeek')">Next Week</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisMonth')">This Month</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('nextMonth')">Next Month</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisYear')">This Year</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('nextYear')">Next Year</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('all')">All</b-button>
      </b-col>
    </b-row>

    <b-alert v-if="error" variant="danger" show class="mt-3">Failed to load maintenance data.</b-alert>

    <b-table id="maintenance-table" class="mt-3" :items="filteredItems" :fields="fields" :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc" :per-page="perPage" :current-page="currentPage" :busy="loading" striped>
      <template #table-busy>
        <div class="d-flex justify-content-center align-items-center my-3">
          <b-spinner small class="mr-2" /> Loading&hellip;
        </div>
      </template>

      <template #cell(name)="{ item }">
        <router-link :to="'/lifts/' + item.id">{{ elevatorName(item) }}</router-link>
      </template>

      <template #cell(stopsUntilService)="{ item }">
        <span v-if="item.stopsUntilService <= 0">Due now</span>
        <span v-else>{{ item.stopsUntilService }}</span>
      </template>

      <template #cell(avgTripsPerDay)="{ item }">
        {{ item.avgTripsPerDay != null ? Math.round(item.avgTripsPerDay) : '' }}
      </template>

      <template #cell(estimatedDate)="{ item }">
        {{ item.estimatedDate || '—' }}<span v-if="item.inaccurate && item.estimatedDate" v-b-tooltip.hover
          :title="`Based on ${item.daysData} days of data`" class="text-muted ml-1" style="cursor:help">~</span>
      </template>

      <template #cell(interval)="{ item }">
        <b-form-input v-if="canManageMaintenance" type="number" min="1" size="sm" :value="item.interval != null ? item.interval : ''"
          :placeholder="String(defaultInterval)" style="width:100px" @change="v => saveInterval(item, v)" @click.stop />
        <span v-else>{{ item.interval != null ? item.interval : defaultInterval }}</span>
      </template>

      <template #cell(effectivePlannedDate)="{ item }">
        <template v-if="canManageMaintenance">
          <div class="d-flex align-items-center" @click.stop>
            <date-picker :value="item.plannedDate || item.suggestedDate || ''" input-style="width:130px" size="sm"
              class="mr-2" :disabled="!!item.plannedDate" @change="v => setPlannedDate(item, v)" />
            <div style="min-width:175px">
              <template v-if="item.plannedDate">
                <b-button size="sm" variant="secondary" @click.stop="unplan(item)">Unschedule</b-button>
                <b-button size="sm" variant="success" class="ml-1" @click.stop="complete(item)">Complete</b-button>
              </template>
              <template v-else>
                <b-button size="sm" variant="primary" :disabled="!pendingDates[item.id] && !item.estimatedDate"
                  @click.stop="plan(item)">Schedule</b-button>
              </template>
            </div>
            <span v-if="item.plannedDate && !isSuggestedDay(item.plannedDate)" v-b-tooltip.hover
              title="Not a suggested day of week" class="ml-2 text-warning" style="cursor:help">⚠</span>
          </div>
        </template>
        <template v-else>
          {{ item.plannedDate || '—' }}
        </template>
      </template>
    </b-table>

    <b-row class="mt-2 align-items-center">
      <b-col cols="auto" v-if="filteredItems.length > 15">
        <b-form-select v-model="perPage" :options="perPageOptions" style="width:auto" />
      </b-col>
      <b-col v-if="filteredItems.length > perPage">
        <b-pagination v-model="currentPage" :total-rows="filteredItems.length" :per-page="perPage"
          aria-controls="maintenance-table" class="mb-0" />
      </b-col>
      <b-col cols="auto" class="ml-auto">
        <span class="text-muted mr-2"><small>iCal</small></span>
        <a :href="icsEstimatesUrl" class="btn btn-secondary btn-sm mr-2" download="maintenance.ics">Estimates</a>
        <a :href="icsScheduledUrl" class="btn btn-secondary btn-sm mr-2" download="maintenance.ics">Scheduled</a>
        <a :href="icsCompletedUrl" class="btn btn-secondary btn-sm" download="maintenance.ics">Completed</a>
      </b-col>
    </b-row>

    <!-- Settings modal -->
    <b-modal v-if="canManageMaintenance" id="maint-settings" title="Maintenance Settings" ok-title="Save" @ok="saveSettings">
      <b-form-group label="Avg. window">
        <b-form-select v-model="settingsWindow" :options="windowOptions" style="width:150px" />
      </b-form-group>
      <b-form-group label="Default interval">
        <b-input-group append="trips" style="width:150px">
          <b-form-input type="number" min="1" v-model.number="settingsInterval" />
        </b-input-group>
      </b-form-group>
      <b-form-group label="Suggested days">
        <b-form-checkbox-group v-model="settingsDays" :options="dayOptions" />
      </b-form-group>
      <b-form-group v-if="isAdmin" label="iCal limit">
        <b-form-input type="number" min="0" v-model.number="icsLimit" placeholder="All" style="width:150px" />
      </b-form-group>
    </b-modal>
  </b-container>
</template>

<script>
import api from './api.js'
import auth from './auth.js'
import formatterMixin from './formatter.mixin.js'
import moment from 'moment'
import DatePicker from './DatePicker.vue'

import ClearableInput from './ClearableInput.vue'
import BrandsFilter from './BrandsFilter.vue'
import brandFilterMixin from './brandFilter.mixin.js'

const WINDOW_KEY = 'maintenanceWindow'
const SORT_BY_KEY = 'maintenanceSortBy'
const SORT_DESC_KEY = 'maintenanceSortDesc'
const SUGGESTED_DAYS_KEY = 'maintenanceSuggestedDays'
const LOCAL_INTERVAL_KEY = 'maintenanceDefaultInterval'
const LOCAL_WINDOW_KEY = 'maintenanceWindowLocal'

export default {
  name: 'Maintenance',
  mixins: [formatterMixin, brandFilterMixin],
  components: { DatePicker, ClearableInput, BrandsFilter },
  props: {
    elevators: { type: Array, default: () => [] },
    settings: { type: Object, default: () => ({}) },
  },
  data() {
    const storedWindow = parseInt(localStorage.getItem(WINDOW_KEY)) || 30
    const sortBy = localStorage.getItem(SORT_BY_KEY) || 'effectivePlannedDate'
    const sortDesc = localStorage.getItem(SORT_DESC_KEY) === 'true'
    const storedDays = localStorage.getItem(SUGGESTED_DAYS_KEY)
    const suggestedDays = storedDays ? JSON.parse(storedDays) : [1, 2, 3, 4, 5]
    const isGroupAdmin = auth.groupAdmin()
    const localInterval = parseInt(localStorage.getItem(LOCAL_INTERVAL_KEY)) || 100000
    const localWindow = parseInt(localStorage.getItem(LOCAL_WINDOW_KEY)) || storedWindow
    return {
      textFilter: (window.history.state && window.history.state.textFilter) || '',
      activePanel: null,
      rawItems: [],
      plannedMap: {},   // elevator_id -> PlannedMaintenance record
      pendingDates: {}, // elevator_id -> typed date string before Plan is pressed
      loading: false,
      error: false,
      window: storedWindow,
      settingsWindow: storedWindow,
      sortBy,
      sortDesc,
      currentPage: 1,
      perPage: parseInt(localStorage.getItem('maintenancePerPage')) || 15,
      perPageOptions: [
        { value: 15, text: '15' },
        { value: 30, text: '30' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      windowOptions: [
        { value: 30, text: '30 days' },
        { value: 60, text: '60 days' },
        { value: 90, text: '90 days' },
      ],
      defaultInterval: isGroupAdmin ? localInterval : 100000,
      _localIntervalOverride: isGroupAdmin,
      icsLimit: parseInt(localStorage.getItem('maintenanceIcsLimit')) || 0,
      localFrom: null,
      localTo: null,
      autoRange: false,
      suggestedDays,
      // settings modal working copies
      settingsInterval: isGroupAdmin ? localInterval : 100000,
      settingsDays: storedDays ? JSON.parse(storedDays) : [1, 2, 3, 4, 5],
      dayOptions: [
        { value: 1, text: 'Mon' },
        { value: 2, text: 'Tue' },
        { value: 3, text: 'Wed' },
        { value: 4, text: 'Thu' },
        { value: 5, text: 'Fri' },
        { value: 6, text: 'Sat' },
        { value: 0, text: 'Sun' },
      ],
    }
  },
  computed: {
    isAdmin() {
      return auth.admin()
    },
    isGroupAdmin() {
      return auth.groupAdmin()
    },
    canManageMaintenance() {
      return this.isAdmin || this.isGroupAdmin
    },
    from: {
      get() { return this.localFrom },
      set(v) { this.localFrom = v },
    },
    to: {
      get() { return this.localTo },
      set(v) { this.localTo = v },
    },
    fromDate() { return this.from ? moment(this.from).format('YYYY-MM-DD') : '' },
    toDate() { return this.to ? moment(this.to).format('YYYY-MM-DD') : '' },
    elevatorBrands() {
      return [...new Set((this.elevators || []).map(e => this.elevatorBrandFormatter(e.type)))].filter(Boolean).sort();
    },
    elevatorModelTypes() {
      return [...new Set((this.elevators || [])
        .filter(e => this.selectedBrands.includes(this.elevatorBrandFormatter(e.type)))
        .map(e => this.elevatorTypeFormatter(e.type) || 'Other'))].filter(Boolean).sort();
    },
    filteredItems() {
      const q = this.textFilter.toLowerCase()
      const from = this.fromDate
      const to = this.toDate
      return this.computedItems
        .filter(item => this.selectedBrands.includes(this.elevatorBrandFormatter(item.type)))
        .filter(item => this.selectedModelTypes.includes(this.elevatorTypeFormatter(item.type) || 'Other'))
        .filter(item => !q || this.elevatorName(item).toLowerCase().includes(q))
        .filter(item => {
          if (!from && !to) return true
          const d = item.effectivePlannedDate
          if (!d) return false
          if (from && d < from) return false
          if (to && d > to) return false
          return true
        })
    },
    fields() {
      const cols = [
        { key: 'name', label: 'Lift', sortable: true },
      ]
      cols.push({ key: 'interval', label: 'Interval', sortable: false })
      cols.push(
        { key: 'stopsUntilService', label: 'Remaining', sortable: true },
        { key: 'avgTripsPerDay', label: 'Per day', sortable: true },
        { key: 'estimatedDate', label: 'Estimate', sortable: true },
        { key: 'effectivePlannedDate', label: 'Schedule', sortable: true },
      )
      return cols
    },
    icsEstimatesUrl() {
      const token = localStorage.getItem('token') || ''
      const base = process.env.NODE_ENV === 'production' ? '' : 'http://192.168.1.5:1323'
      const limit = this.icsLimit ? `&limit=${this.icsLimit}` : ''
      return `${base}/api/v1/maintenance.ics?type=estimates&avg=${this.window}&token=${token}${limit}`
    },
    icsScheduledUrl() {
      const token = localStorage.getItem('token') || ''
      const base = process.env.NODE_ENV === 'production' ? '' : 'http://192.168.1.5:1323'
      const limit = this.icsLimit ? `&limit=${this.icsLimit}` : ''
      return `${base}/api/v1/maintenance.ics?token=${token}${limit}`
    },
    icsCompletedUrl() {
      const token = localStorage.getItem('token') || ''
      const base = process.env.NODE_ENV === 'production' ? '' : 'http://192.168.1.5:1323'
      const limit = this.icsLimit ? `&limit=${this.icsLimit}` : ''
      return `${base}/api/v1/maintenance.ics?type=completed&token=${token}${limit}`
    },
    computedItems() {
      return this.rawItems.map(item => {
        let avg, date, daysData
        if (this.window === 60) {
          avg = item.avgTripsPerDay60; date = item.estimatedDate60; daysData = item.daysData60
        } else if (this.window === 90) {
          avg = item.avgTripsPerDay90; date = item.estimatedDate90; daysData = item.daysData90
        } else {
          avg = item.avgTripsPerDay30; date = item.estimatedDate30; daysData = item.daysData30
        }
        const planned = this.plannedMap[item.id] || {}
        return {
          id: item.id,
          name: item.name,
          type: item.type,
          stopsUntilService: item.stopsUntilService,
          stopsToday: item.stopsToday,
          avgTripsPerDay: avg || null,
          estimatedDate: date || null,
          inaccurate: daysData > 0 && daysData < this.window,
          daysData: daysData || 0,
          interval: planned.interval != null ? planned.interval : null,
          plannedDate: planned.planned_date ? planned.planned_date.substring(0, 10) : null,
          suggestedDate: this.adjustToSuggestedDay(date || null),
          get effectivePlannedDate() { return this.plannedDate || this.suggestedDate || null },
        }
      }).filter(item => item.avgTripsPerDay)
    },
  },
  watch: {
    sortBy(v) { localStorage.setItem(SORT_BY_KEY, v) },
    sortDesc(v) { localStorage.setItem(SORT_DESC_KEY, String(v)) },
    textFilter() {
      const state = Object.assign({}, window.history.state, { textFilter: this.textFilter });
      window.history.replaceState(state, '');
      this.currentPage = 1;
    },
    selBrands() { this.currentPage = 1 },
    selModelTypes() { this.currentPage = 1 },
    perPage(v) { localStorage.setItem('maintenancePerPage', String(v)); this.currentPage = 1 },
    icsLimit(v) { localStorage.setItem('maintenanceIcsLimit', String(v)) },
    settings: {
      immediate: true,
      handler(v) {
        // Group admins use localStorage-only settings; ignore server values.
        if (this._localIntervalOverride) return
        const interval = parseInt(v && v.maintenance_default_interval) || 100000
        this.defaultInterval = interval
        this.settingsInterval = interval
        const w = parseInt(v && v.maintenance_avg_window)
        if (w && [30, 60, 90].includes(w)) {
          this.window = w
          this.settingsWindow = w
          localStorage.setItem(WINDOW_KEY, String(w))
        }
      },
    },
  },
  beforeRouteLeave(to, from, next) {
    history.replaceState(Object.assign({}, history.state, { maintFrom: this.from, maintTo: this.to }), '')
    next()
  },
  mounted() {
    const state = history.state || {}
    if (state.maintFrom) {
      this.from = state.maintFrom
      this.to = state.maintTo
      this.load()
    } else {
      this.load(false, true)
    }
    this.$root.$on('elevator-update', this.onElevatorUpdate)
  },
  beforeDestroy() {
    this.$root.$off('elevator-update', this.onElevatorUpdate)
  },
  methods: {
    onElevatorUpdate(update) {
      // eslint-disable-next-line eqeqeq
      const idx = this.rawItems.findIndex(item => item.id == update.id)
      if (idx === -1) return
      const patched = Object.assign({}, this.rawItems[idx])
      if (update.stopsUntilService !== undefined) patched.stopsUntilService = update.stopsUntilService
      if (update.stopsIncrement !== undefined) patched.stopsToday = (patched.stopsToday || 0) + update.stopsIncrement
      // The server sends avgTripsPerDay and estimatedDate for the configured window.
      // Map them to the matching window-specific fields used by computedItems.
      const w = this.window
      if (update.avgTripsPerDay !== undefined) patched[`avgTripsPerDay${w}`] = update.avgTripsPerDay
      if (update.estimatedDate !== undefined) patched[`estimatedDate${w}`] = update.estimatedDate
      this.$set(this.rawItems, idx, patched)
      if (this.autoRange && (update.estimatedDate !== undefined || update.stopsUntilService !== undefined)) this.setRange('all', true)
    },
    load(silent = false, initRange = false) {
      if (!silent) this.loading = true
      this.error = false
      api.getMaintenancePlan(result => {
        this.loading = false
        if (result.data) {
          this.rawItems = result.data
          if (initRange) this.setRange('all')
        } else {
          this.error = true
        }
      })
      api.getPlannedMaintenances(result => {
        if (result.data) {
          const m = {}
          result.data.forEach(r => { m[r.elevator_id] = r })
          this.plannedMap = m
        }
      })
    },
    setFrom(v) {
      this.autoRange = false
      this.from = v ? moment(v).startOf('day').format() : null
    },
    setTo(v) {
      this.autoRange = false
      this.to = v ? moment(v).endOf('day').format() : null
    },
    setRange(range, preservePage = false) {
      if (range !== 'all') this.autoRange = false
      switch (range) {
        case 'today':
          this.from = moment().startOf('day').format()
          this.to = moment().endOf('day').format()
          break
        case 'tomorrow':
          this.from = moment().add(1, 'day').startOf('day').format()
          this.to = moment().add(1, 'day').endOf('day').format()
          break
        case 'thisWeek':
          this.from = moment().startOf('day').format()
          this.to = moment().endOf('week').format()
          break
        case 'nextWeek':
          this.from = moment().add(1, 'week').startOf('week').format()
          this.to = moment().add(1, 'week').endOf('week').format()
          break
        case 'thisMonth':
          this.from = moment().startOf('day').format()
          this.to = moment().endOf('month').format()
          break
        case 'nextMonth':
          this.from = moment().add(1, 'month').startOf('month').format()
          this.to = moment().add(1, 'month').endOf('month').format()
          break
        case 'thisYear':
          this.from = moment().startOf('day').format()
          this.to = moment().endOf('year').format()
          break
        case 'nextYear':
          this.from = moment().add(1, 'year').startOf('year').format()
          this.to = moment().add(1, 'year').endOf('year').format()
          break
        case 'all': {
          this.autoRange = true
          const dates = this.computedItems.map(i => i.effectivePlannedDate).filter(Boolean).sort()
          this.from = dates.length ? moment(dates[0]).startOf('day').format() : null
          this.to = dates.length ? moment(dates[dates.length - 1]).endOf('day').format() : null
          break
        }
      }
      if (!preservePage) this.currentPage = 1
    },
    onWindowChange(v) {
      localStorage.setItem(WINDOW_KEY, String(v))
      this.currentPage = 1
    },
    saveSettings() {
      this.defaultInterval = this.settingsInterval
      this.suggestedDays = this.settingsDays.slice()
      this.window = this.settingsWindow
      localStorage.setItem(WINDOW_KEY, String(this.settingsWindow))
      this.currentPage = 1
      if (this._localIntervalOverride) {
        // Group admin: persist to localStorage only.
        localStorage.setItem(LOCAL_INTERVAL_KEY, String(this.settingsInterval))
        localStorage.setItem(LOCAL_WINDOW_KEY, String(this.settingsWindow))
      } else {
        api.putSettings({
          maintenance_default_interval: String(this.settingsInterval),
          maintenance_avg_window: String(this.settingsWindow),
        }, () => { })
      }
      localStorage.setItem(SUGGESTED_DAYS_KEY, JSON.stringify(this.settingsDays))
    },
    isSuggestedDay(dateStr) {
      if (!dateStr) return true
      const d = new Date(dateStr)
      return this.suggestedDays.includes(d.getDay())
    },
    adjustToSuggestedDay(dateStr) {
      if (!dateStr || !this.suggestedDays.length) return dateStr
      const [y, m, d] = dateStr.split('-').map(Number)
      const date = new Date(y, m - 1, d)
      let attempts = 0
      while (!this.suggestedDays.includes(date.getDay()) && attempts < 7) {
        date.setDate(date.getDate() + 1)
        attempts++
      }
      const yy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      return `${yy}-${mm}-${dd}`
    },
    saveInterval(item, value) {
      const interval = value !== '' ? parseInt(value) : null
      const planned = this.plannedMap[item.id] || {}
      const data = { interval, planned_date: planned.planned_date ? planned.planned_date.substring(0, 10) : null }
      api.putPlannedMaintenance(item.id, data, result => {
        if (result.data) {
          this.$set(this.plannedMap, item.id, result.data)
        }
      })
    },
    setPlannedDate(item, value) {
      this.$set(this.pendingDates, item.id, value)
    },
    plan(item) {
      const dateStr = this.pendingDates[item.id] || item.suggestedDate || item.estimatedDate
      if (!dateStr) return
      const planned = this.plannedMap[item.id] || {}
      const data = { interval: planned.interval != null ? planned.interval : null, planned_date: dateStr }
      api.putPlannedMaintenance(item.id, data, result => {
        if (result.data) {
          this.$set(this.plannedMap, item.id, result.data)
          this.$delete(this.pendingDates, item.id)
          if (this.autoRange) this.$nextTick(() => this.setRange('all'))
        }
      })
    },
    unplan(item) {
      api.deletePlannedMaintenance(item.id, () => {
        const existing = this.plannedMap[item.id]
        if (existing && existing.interval != null) {
          this.$set(this.plannedMap, item.id, Object.assign({}, existing, { planned_date: null }))
        } else {
          this.$delete(this.plannedMap, item.id)
        }
        if (this.autoRange) this.$nextTick(() => this.setRange('all'))
      })
    },
    complete(item) {
      const interval = item.interval != null ? item.interval : this.defaultInterval
      api.completeMaintenance(item.id, { interval }, () => {
        this.load(true, this.autoRange)
      })
    },
    dueDateClass(date) {
      if (!date) return ''
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const d = new Date(date)
      const diffDays = Math.floor((d - today) / 86400000)
      if (diffDays <= 0) return 'font-weight-bold'
      return ''
    },
  },
}
</script>

<style scoped>
#maintenance-table>>>td {
  vertical-align: middle;
}

.filter-panel {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.35s ease;
}

.filter-panel-open {
  max-height: 1000px;
}

.filter-panel-content {
  padding-left: 15px;
  padding-bottom: 0.75rem;
}
</style>



// WEBPACK FOOTER //
// src/Maintenance.vue